import apiClient from '@/api/client';

/**
 * Budget planning, spend control and purchasing (backend build 2026-10-24).
 * Money values are strings (Decimal on the server); format with `useMoney`.
 * Mutations send an Idempotency-Key. Record-creating calls (open cycle, request, transfer, saved
 * forecast, Excel import) take a key that stays the same for the same body until it succeeds, so a
 * person retrying after a timeout gets the first result back instead of a duplicate.
 */

export type Money = string;
export type VersionStatus =
  | 'not_started' | 'draft' | 'in_review' | 'sent_back' | 'approved' | 'locked' | 'archived' | 'superseded';

export interface Viewer {
  role_label: string;
  is_finance: boolean;
  can_admin: boolean;
  can_write: boolean;
  can_submit: boolean;
  can_approve: boolean;
  can_transfer: boolean;
  can_exec: boolean;
  can_audit_export: boolean;
  can_purchase: boolean;
  can_purchasing_read: boolean;
  can_order: boolean;
  head_of: string[];
  is_admin: boolean;
}

export interface StepView {
  id?: string;
  step_order?: number;
  role: string;
  reason?: string | null;
  status: 'waiting' | 'pending' | 'approved' | 'rejected' | 'skipped' | 'expired' | 'withdrawn' | 'not_yet';
  decided_by_name?: string | null;
  decided_at?: string | null;
  note?: string | null;
  due_at?: string | null;
  hours_in_step?: number | null;
  sla_hours?: number;
  breached?: boolean;
  hours_left?: number | null;
  delegated?: boolean;
  assigned_user_id?: string | null;
}

export interface DepartmentRow {
  id: string;
  name: string;
  version_id: string;
  owner_name: string | null;
  director_name: string | null;
  status: VersionStatus;
  started: boolean;
  target: Money;
  total: Money;
  requested: Money;
  pct_of_target: string | null;
  payroll: Money;
  operating: Money;
  depreciation: Money;
  capex_cash: Money;
  last_year: Money;
  hires_planned: number;
  headcount_by_month: number[];
  monthly: Money[];
  steps: StepView[];
  n_steps: number;
  current_step: number | null;
  current_step_id: string | null;
  can_act: boolean;
  next_action: { kind: string; role?: string | null };
}

export interface Fact { kind: string; [k: string]: unknown }

export interface CycleModel {
  cycle: {
    id: string; fiscal_year: number; name: string; status: string; stage_index: number;
    seed_method: string; currency: string; attention_pct: string; overspend_policy: string;
    cfo_threshold: Money; cost_of_revenue_pct: string; due_at: string | null; board_at: string | null;
    opened_at: string | null; locked_at: string | null; days_to_deadline: number | null; all_approved: boolean;
  };
  assumptions: {
    pay_rise_pct: string; inflation_pct: string; hire_delay_months: number; pension_employer_pct: string;
    health_cover_per_head: Money; price_rise_pct: string; churn_pct: string;
    fx_plan: Record<string, string>; fx_actual: Record<string, string>;
  };
  chain: { step_order: number; role: string; resolver: string; sla_hours: number; use_cfo_threshold: boolean; delegated: boolean }[];
  departments: DepartmentRow[];
  totals: {
    target: Money; requested: Money; approved: Money; payroll: Money; last_year: Money;
    departments: number; in_chain_or_approved: number; pending: number; headcount_start: number; headcount_end: number;
  };
  monthly_plan: Money[];
  headcount_by_month: number[];
  facts: Fact[];
  activity: { event: string; at: string; actor_name: string | null; data: Record<string, unknown> }[];
  viewer: Viewer;
}

export interface SubItemView { id: string; name: string; note: string | null; months: Money[]; quarters: Money[]; total: Money }

export interface LineView {
  id: string | null; key: string; name: string; kind: string; account_id: string | null;
  billing_currency: string | null; last_year: Money; uplift_pct: string | null; months: Money[];
  quarters: Money[]; total: Money; change_pct: string | null; typed_months: number[]; source: string;
  /** Operating lines only: a broken-down line's figures are the sum of its items. */
  has_items?: boolean; items?: SubItemView[];
}

export interface DepartmentView {
  version: { id: string; name: string; status: VersionStatus; fiscal_year: number; growth_pct: string; seed_method: string; currency: string; updated_at: string | null };
  department: { id: string; name: string | null; owner_name: string | null; director_name: string | null };
  cycle: { id: string; due_at: string | null; status: string; inflation_pct: string; pay_rise_pct: string; hire_delay_months: number };
  target: Money;
  totals: { total: Money; payroll: Money; operating: Money; depreciation: Money; capex_cash: Money; last_year: Money; monthly: Money[]; quarters: Money[]; change_pct: string | null };
  payroll: LineView[];
  operating: LineView[];
  depreciation: LineView;
  capex: (LineView & { life_months: number; start_month: number; amount: Money; depreciation_this_year: Money; account_label: string | null })[];
  asset_accounts: { id: string; label: string }[];
  in_use_version: { id: string; fiscal_year: number; status: string } | null;
  headcount: { current: number; by_month: number[]; hires: { id: string; title: string; monthly_cost: Money; start_month: number; included: boolean; requisition_ref: string | null }[] };
  steps: StepView[];
  pending_step_id: string | null;
  suggestion: null | { kind: 'reach_target'; growth_from: string; growth_to: string; hire_start_month: number; suggested_total: Money; reaches_target: boolean; remaining_gap: Money } | { kind: 'under_target'; headroom: Money } | { kind: 'reach_exhausted'; remaining_gap: Money };
  comments: { id: string; author_name: string; author_role: string | null; body: string; created_at: string }[];
  staff_suggestions: { id: string; by: string; description: string; justification: string | null; amount: Money | null; month: number | null; status: string; created_at: string }[];
  permissions: { can_edit: boolean; can_submit: boolean; submit_action: 'submit' | 'resubmit' | 'withdraw' | null; can_comment: boolean };
}

export interface SpendLine {
  line_id: string; name: string; is_payroll: boolean; kind: string; billing_currency: string | null;
  account_id: string | null; project_id: string | null; allocated: Money; spent: Money; committed: Money; remaining: Money;
  used_pct: string; pace_pct: string;
  hold: null | { id: string; status: string; triggered_pct: string; triggered_at: string };
  reviewed: boolean; alert: 'over' | 'hold' | 'reviewed' | 'ok';
}

export interface SpendView {
  fiscal_year: number;
  departments: { id: string; name: string; has_budget: boolean }[];
  legacy_company_level?: boolean;
  version: null | { id: string; name: string; status: string; department_id: string | null; currency: string };
  policy?: 'warn' | 'block' | 'transfer';
  attention_pct?: string;
  cfo_threshold?: Money;
  year_elapsed: string;
  months_left?: number;
  lines: SpendLine[];
  projects?: { id: string; name: string }[];
  transfers?: { id: string; from_line: string; to_line: string; amount: Money; status: string; reason: string; created_at: string; pending_step_id: string | null; pending_role: string | null; can_act: boolean }[];
  fx_split?: { line: string; currency: string; plan_rate: string; actual_rate: string; rate_effect: Money; volume_effect: Money; total: Money }[];
  postings?: { date: string | null; source: string; description: string; account: string | null; amount: Money }[];
  can_set_policy?: boolean;
  can_release?: boolean;
  can_request_transfer?: boolean;
}

export interface SpendDecision {
  outcome: 'allow' | 'warn' | 'hold' | 'block';
  reason: string;
  remaining_before?: Money;
  remaining_after?: Money;
  used_pct_after?: string;
  opens_hold?: boolean;
  notify_cfo?: boolean;
  needs_transfer?: boolean;
  shortfall?: Money;
  line?: { id: string; name: string; allocated: Money };
}

/** Idempotency header. State transitions (submit, decide, order, pay…) are safe to retry on the
 *  server, so a fresh key per call is enough for them. Calls that CREATE a record take the key
 *  from `useActionKey` instead, so a retry after a timeout replays rather than duplicates. */
const idem = (key?: string) => ({ headers: { 'Idempotency-Key': key ?? crypto.randomUUID() } });
const B = '/budget';

export const budgetingApi = {
  // Silent: the sidebar, the /finance guard and the portal ask this for everyone; no access is a
  // normal answer there, not an error to toast.
  current: () => apiClient.get(`${B}/cycles/current`, { _silentOnError: true } as any).then((r) => r.data as {
    cycle: null | { id: string; fiscal_year: number; name: string; status: string };
    can_open: boolean; settings: Record<string, string>; viewer: Viewer;
  }),
  openCycle: (body: { fiscal_year: number; seed_method: string; due_at?: string; board_at?: string; pay_rise_pct?: string; inflation_pct?: string }, key?: string) =>
    apiClient.post(`${B}/cycles`, body, idem(key)).then((r) => r.data as { id: string }),
  model: (cycleId: string) => apiClient.get(`${B}/cycles/${cycleId}/model`).then((r) => r.data as CycleModel),
  patchAssumptions: (cycleId: string, body: Record<string, unknown>) =>
    apiClient.patch(`${B}/cycles/${cycleId}/assumptions`, body).then((r) => r.data),
  putTargets: (cycleId: string, targets: { department_id: string; amount: string; note?: string }[]) =>
    apiClient.put(`${B}/cycles/${cycleId}/targets`, { targets }).then((r) => r.data),
  lock: (cycleId: string) => apiClient.post(`${B}/cycles/${cycleId}/lock`, {}, idem()).then((r) => r.data),
  remind: (cycleId: string) => apiClient.post(`${B}/cycles/${cycleId}/remind`).then((r) => r.data as { reminded: number }),
  approvalQueue: (cycleId: string) => apiClient.get(`${B}/cycles/${cycleId}/approvals`).then((r) => r.data as { queue: any[] }),
  versionFor: (cycleId: string, departmentId: string) =>
    apiClient.get(`${B}/cycles/${cycleId}/departments/${departmentId}/version`).then((r) => r.data as { version_id: string }),

  version: (id: string) => apiClient.get(`${B}/versions/${id}`).then((r) => r.data as DepartmentView),
  editCell: (id: string, body: { line_id: string; quarter?: number; month?: number; amount: string | null }) =>
    apiClient.patch(`${B}/versions/${id}/cells`, body).then((r) => r.data as DepartmentView),
  breakDown: (id: string, lineId: string) =>
    apiClient.post(`${B}/versions/${id}/lines/${lineId}/break-down`, {}, idem()).then((r) => r.data as DepartmentView),
  addItem: (id: string, lineId: string, name: string) =>
    apiClient.post(`${B}/versions/${id}/lines/${lineId}/items`, { name }, idem()).then((r) => r.data as DepartmentView),
  renameItem: (id: string, lineId: string, itemId: string, body: { name?: string; note?: string }) =>
    apiClient.patch(`${B}/versions/${id}/lines/${lineId}/items/${itemId}`, body).then((r) => r.data as DepartmentView),
  editItemCell: (id: string, lineId: string, itemId: string, body: { quarter?: number; month?: number; amount: string | null }) =>
    apiClient.patch(`${B}/versions/${id}/lines/${lineId}/items/${itemId}/cells`, body).then((r) => r.data as DepartmentView),
  deleteItem: (id: string, lineId: string, itemId: string) =>
    apiClient.delete(`${B}/versions/${id}/lines/${lineId}/items/${itemId}`).then((r) => r.data as DepartmentView),
  editUplift: (id: string, body: { line_id: string; uplift_pct: string | null }) =>
    apiClient.patch(`${B}/versions/${id}/uplift`, body).then((r) => r.data as DepartmentView),
  editGrowth: (id: string, growth_pct: string) =>
    apiClient.patch(`${B}/versions/${id}/growth`, { growth_pct }).then((r) => r.data as DepartmentView),
  reseed: (id: string, seed_method: string) =>
    apiClient.post(`${B}/versions/${id}/seed`, { seed_method }).then((r) => r.data as DepartmentView),
  editHire: (id: string, hireId: string, body: { included?: boolean; start_month?: number; monthly_cost?: string }) =>
    apiClient.patch(`${B}/versions/${id}/hires/${hireId}`, body).then((r) => r.data as DepartmentView),
  addCapex: (id: string, body: { name: string; amount: string; life_months: number; start_month: number; account_id?: string | null }) =>
    apiClient.post(`${B}/versions/${id}/capex`, body).then((r) => r.data as DepartmentView),
  editCapex: (id: string, lineId: string, body: { name: string; amount: string; life_months: number; start_month: number; account_id?: string | null }) =>
    apiClient.put(`${B}/versions/${id}/capex/${lineId}`, body).then((r) => r.data as DepartmentView),
  deleteCapex: (id: string, lineId: string) =>
    apiClient.delete(`${B}/versions/${id}/capex/${lineId}`).then((r) => r.data as DepartmentView),
  applySuggestion: (id: string) => apiClient.post(`${B}/versions/${id}/apply-suggestion`).then((r) => r.data as DepartmentView),
  submit: (id: string) => apiClient.post(`${B}/versions/${id}/submit`, {}, idem()).then((r) => r.data as DepartmentView),
  withdraw: (id: string) => apiClient.post(`${B}/versions/${id}/withdraw`, {}, idem()).then((r) => r.data as DepartmentView),
  comment: (id: string, body: string) => apiClient.post(`${B}/versions/${id}/comments`, { body }).then((r) => r.data),

  decide: (stepId: string, decision: 'approve' | 'send_back', note?: string) =>
    apiClient.post(`${B}/approvals/${stepId}/decide`, { decision, note: note || null }, idem()).then((r) => r.data as { subject_type: string; status: string }),

  spend: (fiscalYear: number, departmentId?: string | null) =>
    apiClient.get(`${B}/spend`, { params: { fiscal_year: fiscalYear, department_id: departmentId || undefined } }).then((r) => r.data as SpendView),
  /** Without an amount, a payroll run checks this month of the payroll line and a requisition
   *  the role's cost for the rest of the year (worked out by the API, returned as amount/about). */
  checkSpend: (body: { line_id: string; amount?: string; source: string }) =>
    apiClient.post(`${B}/check-spend`, body).then((r) => r.data as SpendDecision & { amount?: Money; about?: { month?: number; requisition?: string; title?: string; basis?: string } | null }),
  /** Transfers waiting for this person's decision, across departments (Approvals tab, mobile). */
  pendingTransfers: (fiscalYear: number) =>
    apiClient.get(`${B}/transfers/pending`, { params: { fiscal_year: fiscalYear } }).then((r) => r.data as { items: (NonNullable<SpendView['transfers']>[number] & { department: string | null })[]; currency: string | null }),
  releaseHold: (holdId: string, outcome: string) =>
    apiClient.post(`${B}/holds/${holdId}/release`, { outcome }).then((r) => r.data),
  requestTransfer: (body: { from_line_id: string; to_line_id: string; amount: string; reason?: string }, key?: string) =>
    apiClient.post(`${B}/transfers`, body, idem(key)).then((r) => r.data),

  settings: () => apiClient.get(`${B}/settings`).then((r) => r.data as {
    settings: Record<string, string>; cycle_id: string | null; can_edit: boolean;
    budget_chain: { step_order: number; role: string; resolver: string; sla_hours: number; use_cfo_threshold: boolean }[];
    routing_rules: { id: string; priority: number; step_role: string; when: string; amount?: string | null; category?: string; attention_pct?: string }[];
  }),
  patchSettings: (body: Record<string, string>) => apiClient.patch(`${B}/settings`, body).then((r) => r.data),

  suggest: (body: { description: string; justification?: string; amount?: string; month?: number; account_id?: string }) =>
    apiClient.post(`${B}/suggestions`, body).then((r) => r.data),
  mySuggestions: () => apiClient.get(`${B}/suggestions/mine`).then((r) => r.data as { items: any[] }),
  decideSuggestion: (id: string, accept: boolean, note?: string) =>
    apiClient.post(`${B}/suggestions/${id}/decide`, { accept, note: note || null }).then((r) => r.data),

  analytics: (cycleId: string) => apiClient.get(`${B}/cycles/${cycleId}/analytics`).then((r) => r.data),
  yearAnalytics: (fiscalYear: number) => apiClient.get(`${B}/year-analytics`, { params: { fiscal_year: fiscalYear } }).then((r) => r.data),
  forecast: (fiscalYear: number, version: string, scenario: string) =>
    apiClient.get(`${B}/forecast`, { params: { fiscal_year: fiscalYear, version, scenario } }).then((r) => r.data),
  saveForecastVersion: (fiscal_year: number, name: string, key?: string) =>
    apiClient.post(`${B}/forecast/versions`, { fiscal_year, name }, idem(key)).then((r) => r.data),
  setLineProject: (lineId: string, projectId: string | null) =>
    apiClient.put(`${B}/lines/${lineId}/project`, { project_option_id: projectId }).then((r) => r.data as { line_id: string; project_id: string | null }),
  revenue: (cycleId: string) => apiClient.get(`${B}/cycles/${cycleId}/revenue`).then((r) => r.data),
  editRevenueLine: (lineId: string, growth_pct: string) =>
    apiClient.patch(`${B}/revenue-lines/${lineId}`, { growth_pct }).then((r) => r.data),
  projections: (fiscalYear: number) => apiClient.get(`${B}/projections`, { params: { fiscal_year: fiscalYear } }).then((r) => r.data),
  savings: (cycleId: string) => apiClient.get(`${B}/cycles/${cycleId}/savings`).then((r) => r.data as { items: any[]; can_decide: boolean }),
  refreshSavings: (cycleId: string) => apiClient.post(`${B}/cycles/${cycleId}/savings/refresh`, {}, { _silentOnError: true } as any).then((r) => r.data as { items: any[]; can_decide: boolean }),
  decideSaving: (id: string, accept: boolean, note?: string) =>
    apiClient.post(`${B}/savings/${id}/decide`, { accept, note: note || null }, idem()).then((r) => r.data),
  narrative: (cycleId: string, kind: string, versionId?: string, fiscalYear?: number) =>
    apiClient.post(`${B}/cycles/${cycleId}/narratives/${kind}`, { ...(versionId ? { version_id: versionId } : {}), ...(fiscalYear ? { fiscal_year: fiscalYear } : {}) }, { _silentOnError: true } as any).then((r) => r.data as { ai: boolean; sentences?: string[]; model?: string }),
  alerts: () => apiClient.get(`${B}/alerts`).then((r) => r.data as { rules: { rule: string; enabled: boolean; channels: string[]; params: Record<string, string> }[]; would_fire: any[]; can_edit: boolean }),
  editAlertRule: (rule: string, body: { enabled?: boolean; channels?: string[] }) =>
    apiClient.patch(`${B}/alerts/rules/${rule}`, body).then((r) => r.data),
  editAlertChannels: (channels: string[]) => apiClient.patch(`${B}/alerts/channels`, { channels }).then((r) => r.data),
  group: () => apiClient.get(`${B}/group`).then((r) => r.data),
  auditPreview: (cycleId: string) =>
    apiClient.get(`${B}/cycles/${cycleId}/audit-export`, { params: { preview: true } }).then((r) => r.data as { csv: string; signature: string; rows: number }),
  executive: (cycleId: string) => apiClient.get(`${B}/cycles/${cycleId}/executive`).then((r) => r.data),
  reports: () => apiClient.get(`${B}/reports`).then((r) => r.data as { items: { key: string; schedule_type: string }[] }),
  scheduleReport: (report: string, recipients: string[], schedule_cron?: string) =>
    apiClient.post(`${B}/reports/schedule`, { report, recipients, schedule_cron }).then((r) => r.data),
  excelPreview: (versionId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post(`${B}/versions/${versionId}/excel/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data as {
      filename: string; counts: Record<string, number>;
      rows: { row: number; name: string; account: string; line_id: string | null; current_total: string | null; quarters: (string | null)[]; file_total: string; status: 'ready' | 'confirm' | 'error' | 'ignored'; reason: string; quarters_flagged?: number[]; ratio_pct?: string; map_options?: { line_id: string; name: string }[] }[];
    });
  },
  excelConfirm: (versionId: string, rows: Record<string, unknown>[], key?: string) =>
    apiClient.post(`${B}/versions/${versionId}/excel/confirm`, { rows }, idem(key)).then((r) => r.data as { applied: number; skipped: number; held_for_finance: number }),
};

/** URL for a file download (Excel/PDF/CSV) — opened with the session cookie. */
export function budgetFileUrl(path: string, params: Record<string, string | number | undefined> = {}) {
  const base = `${import.meta.env.VITE_API_URL ?? ''}/api/v1${B}${path}`;
  const q = Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return q.length ? `${base}?${q.join('&')}` : base;
}

// ── Purchasing ──────────────────────────────────────────────────────────────

const P = '/budget/purchasing';

export interface PurchaseRow {
  id: string; ref: string; description: string; requester_name: string; department_id: string;
  line: { id: string; name: string | null }; amount: Money; currency: string; category: string;
  vendor: string | null; vendor_id: string | null; status: string;
  stage: 'draft' | 'request' | 'approved' | 'order' | 'received' | 'bill' | 'paid' | 'rejected' | 'withdrawn' | 'cancelled' | string;
  effect: { kind: string; amount?: Money };
  route: StepView[]; pending_step_id: string | null; can_decide: boolean;
  order: null | { id: string; ref: string; status: string };
  vendor_bill: null | { id: string; status: string; receipt_id: string | null; amount: Money | null; invoice_number: string | null };
  match: { result: string | null; received_qty?: string; ordered_qty?: string };
  needed_by: string | null; created_at: string; info_question: string | null; info_answer: string | null;
}

export interface PurchaseDetail extends PurchaseRow {
  justification: string | null;
  grns: { id: string; condition: string; quantity: string; amount: Money; by: string; at: string; note: string | null }[];
  comments: { id: string; author_name: string; body: string; created_at: string }[];
  timeline: { stage: string; at: string | null; done: boolean }[];
  waiting_on: null | { role: string; name: string | null };
  can_withdraw: boolean; can_answer: boolean; can_confirm_delivery: boolean; can_order: boolean; order_id: string | null;
}

export interface AttachmentReading {
  status: 'pending' | 'extracting' | 'extracted' | 'failed' | 'skipped'; failed: boolean;
  vendor_name: string | null; vendor_id: string | null; matched_vendor_name: string | null;
  amount: number | null; currency: string | null; date: string | null; invoice_number: string | null;
}

export const purchasingApi = {
  lines: (fiscalYear: number, departmentId?: string | null) =>
    apiClient.get(`${P}/lines`, { params: { fiscal_year: fiscalYear, department_id: departmentId || undefined } }).then((r) => r.data as {
      department_id: string | null; departments: { id: string; name: string }[]; finance_view?: boolean;
      lines: { id: string; name: string; kind: string; account_id: string | null; on_hold: boolean; remaining?: Money; allocated?: Money; room?: boolean }[];
    }),
  preview: (body: { budget_line_id: string; amount: string; category: string; vendor_id?: string | null; proposed_vendor_name?: string | null }) =>
    apiClient.post(`${P}/preview`, body, { _silentOnError: true } as any).then((r) => r.data as {
      decision: SpendDecision; room: 'room_on_line' | 'on_review_hold' | 'needs_extra_approval';
      line: { id: string; name: string; remaining?: Money; allocated?: Money };
      route: { role: string; why: string }[]; blocked: boolean; vendor_needs_approval: boolean; attention_pct: string;
    }),
  create: (body: Record<string, unknown>, key?: string) => apiClient.post(`${P}/requests`, body, idem(key)).then((r) => r.data as PurchaseDetail),
  /** Quote or delivery photo (PDF/image, 10 MB); returns the receipt id to send with the request. */
  attach: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post(`${P}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data as { receipt_id: string; filename: string });
  },
  /** What the receipts AI read from an attachment (only the few fields the form fills). */
  reading: (receiptId: string) =>
    apiClient.get(`${P}/attachments/${receiptId}`, { _silentOnError: true } as any).then((r) => r.data as AttachmentReading),
  list: (view: string, departmentId?: string | null, page = 1) =>
    apiClient.get(`${P}/requests`, { params: { view, department_id: departmentId || undefined, page, per_page: 50 } }).then((r) => r.data as { items: PurchaseRow[]; total: number; page: number; total_pages: number }),
  get: (id: string) => apiClient.get(`${P}/requests/${id}`).then((r) => r.data as PurchaseDetail),
  submit: (id: string) => apiClient.post(`${P}/requests/${id}/submit`, {}, idem()).then((r) => r.data as PurchaseDetail),
  withdraw: (id: string) => apiClient.post(`${P}/requests/${id}/withdraw`).then((r) => r.data as PurchaseDetail),
  requestInfo: (id: string, text: string) => apiClient.post(`${P}/requests/${id}/request-info`, { text }).then((r) => r.data as PurchaseDetail),
  answer: (id: string, text: string) => apiClient.post(`${P}/requests/${id}/answer`, { text }).then((r) => r.data as PurchaseDetail),
  comment: (id: string, body: string, mentions: string[] = []) => apiClient.post(`${P}/requests/${id}/comments`, { body, mentions }).then((r) => r.data as PurchaseDetail),
  /** People who can be @mentioned on a request (everyone already involved in it). */
  people: (id: string) => apiClient.get(`${P}/requests/${id}/people`).then((r) => r.data as { items: { id: string; name: string }[] }),
  order: (id: string, body: { vendor_id?: string | null; expected_at?: string | null } = {}) =>
    apiClient.post(`${P}/requests/${id}/order`, body, idem()).then((r) => r.data as PurchaseDetail),
  send: (poId: string) => apiClient.post(`${P}/orders/${poId}/send`).then((r) => r.data as PurchaseDetail),
  receive: (poId: string, body: { condition: 'full' | 'partial' | 'damaged'; quantity: string; amount?: string | null; note?: string | null; photo_receipt_id?: string | null }) =>
    apiClient.post(`${P}/orders/${poId}/receive`, body, idem()).then((r) => r.data as PurchaseDetail),
  bill: (poId: string, body: { amount: string; invoice_number?: string | null; receipt_id?: string | null; accept_variance?: boolean }) =>
    apiClient.post(`${P}/orders/${poId}/bill`, body, idem()).then((r) => r.data as PurchaseDetail),
  pay: (poId: string) => apiClient.post(`${P}/orders/${poId}/pay`, {}, idem()).then((r) => r.data as PurchaseDetail),
  cancel: (poId: string, reason: string) => apiClient.post(`${P}/orders/${poId}/cancel`, { reason }).then((r) => r.data as PurchaseDetail),
  overview: (fiscalYear: number, departmentId?: string | null) =>
    apiClient.get(`${P}/overview`, { params: { fiscal_year: fiscalYear, department_id: departmentId || undefined } }).then((r) => r.data as {
      kpis: { requests_waiting: number; avg_approval_days: string | null; open_orders: number; committed: Money; billed_through_purchasing: Money; billed_share_pct: string | null; lines_on_hold: number };
      routing: { rules: { id: string; step_role: string; when: string; amount?: string | null; category?: string; attention_pct?: string }[]; policy: string; attention_pct: string; cfo_threshold: Money };
    }),
  vendors: (fiscalYear: number, departmentId?: string | null) =>
    apiClient.get(`${P}/vendors`, { params: { fiscal_year: fiscalYear, department_id: departmentId || undefined } }).then((r) => r.data as { items: { id: string; name: string; kind: string; code: string; spent: Money | null; open_orders: Money | null; terms_days: number | null; status: string; approved: boolean; portal_enabled: boolean }[] }),
};
